import React, { useState } from 'react';
import { Box, Typography, useMediaQuery, useTheme } from '@mui/material';
import { CardSection, TextIcon } from '@europrocurement/flexy-components';
import { dateToFrenchFormat } from '@europrocurement/l2d-utils';
import {
    UPDATE_CURRENT_COLLECTION_CTX,
    useCurrentCollectionCtx,
    useCurrentCollectionCtxDispatch,
} from '@europrocurement/l2d-modules/providers';
import { useFormaliteService } from '@europrocurement/l2d-domain';
import { useSnackbar } from 'notistack';
import EditablePriorityChip from '../../../../Dossiers/components/detail/EditablePriorityChip/EditablePriorityChip';
import InfoCardOrder from '../InfoCardOrder/InfoCardOrder';

export type OrderInformationsProps = {
    dateCreate?: string | number;
    dateUpdate?: string | number;
};

const OrderInformations: React.FunctionComponent<OrderInformationsProps> = function ({
    dateCreate,
    dateUpdate,
}) {
    const { palette, breakpoints } = useTheme();
    const { collection } = useCurrentCollectionCtx();
    const dispatch = useCurrentCollectionCtxDispatch();
    const { collectionApi } = useFormaliteService();
    const { enqueueSnackbar } = useSnackbar();
    const [isLoading, setIsLoading] = useState<boolean>(false);

    const handlePriorityChange = async (newCritical: boolean) => {
        if (isLoading) {
            return;
        }
        if (!collection || !collection.id) {
            enqueueSnackbar('Id de commande non trouvée', { variant: 'error' });
            return;
        }
        setIsLoading(true);
        try {
            const { status } = await collectionApi.apiCollectionsDossiersIdPatch({
                id: collection.id.toString(),
                collectionDeDossierCollectiondossierUpdate: {
                    urgent: newCritical,
                },
            });

            if (dispatch && status === 200 && collection) {
                dispatch({
                    type: UPDATE_CURRENT_COLLECTION_CTX,
                    payload: { ...collection, critical: newCritical },
                });
            }
        } catch (err) {
            console.warn(err);
            enqueueSnackbar('Une erreur est survenue lors de la mise à jour de la commande', {
                variant: 'error',
            });
        }

        setIsLoading(false);
    };

    const downMdBreakpoint = useMediaQuery(breakpoints.down('md'));

    const infoCardSx = {
        padding: downMdBreakpoint ? '16px 32px' : '8px 32px',
        borderRight: downMdBreakpoint ? 'none' : `solid 1px ${palette.grey[300]}`,
        '&:last-of-type': {
            borderRight: 'none',
        },
    };
    const textStyle = {
        fontSize: '13px',
        fontWeight: '500',
        lineHeight: '19.2px',
        color: palette.grey[500],
    };

    const cardHeader = (
        <Box>
            <Typography variant="l2dh6">Traitement de la commande</Typography>
        </Box>
    );

    return (
        <CardSection
            header={cardHeader}
            sxHeader={{ padding: '24px 32px 8px 32px' }}
            sx={{ marginTop: '8px' }}
            sxContent={{ padding: '0 0 24px 0' }}
        >
            <Box
                sx={{
                    display: 'grid',
                    gridTemplateColumns: downMdBreakpoint ? '1fr' : 'repeat(4, 1fr)',
                    gridTemplateRows: downMdBreakpoint ? 'repeat(2, 1fr)' : '1fr',
                    marginTop: '16px',
                    width: '100%',
                }}
            >
                <InfoCardOrder
                    label="Première création"
                    sx={infoCardSx}
                >
                    <TextIcon
                        icon={{ props: { icon: ['fasl', 'calendar'] } }}
                        gap="6px"
                    >
                        <Typography
                            sx={textStyle}
                            component="span"
                        >
                            {dateCreate ? dateToFrenchFormat(new Date(dateCreate)) : ' - '}
                        </Typography>
                    </TextIcon>
                </InfoCardOrder>
                <InfoCardOrder
                    label="Dernière mise à jour"
                    sx={infoCardSx}
                >
                    <TextIcon
                        icon={{ props: { icon: ['fasl', 'calendar'] } }}
                        gap="6px"
                    >
                        <Typography
                            sx={textStyle}
                            component="span"
                        >
                            {dateUpdate ? dateToFrenchFormat(new Date(dateUpdate)) : ' - '}
                        </Typography>
                    </TextIcon>
                </InfoCardOrder>

                <InfoCardOrder
                    label="Priorité "
                    sx={infoCardSx}
                >
                    <EditablePriorityChip
                        critical={collection?.critical || false}
                        onPriorityChange={handlePriorityChange}
                        isLoading={isLoading}
                        fontSx={{ fontSize: '13px' }}
                    />
                </InfoCardOrder>
            </Box>
        </CardSection>
    );
};

export default OrderInformations;
